'use client';

import { useState, useEffect } from 'react';
import { 
  ChevronLeft, 
  ChevronRight, 
  DollarSign, 
  X
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { supabase } from '@/lib/supabase';

// Type definitions for calendar data
interface CalendarSetting {
  date: string;
  base_price: string;
  is_available: boolean;
  is_reserved: boolean;
  notes: string;
}

interface CalendarBooking {
  id: string;
  check_in: string;
  check_out: string;
  status: string;
}

interface CalendarData {
  bookings: CalendarBooking[];
  settings: CalendarSetting[];
}

interface AdminCalendarProps {
  sessionToken: string;
}

const AdminCalendar = ({ sessionToken }: AdminCalendarProps): JSX.Element => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [calendarData, setCalendarData] = useState<CalendarData>({
    bookings: [],
    settings: []
  });
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const [settingsForm, setSettingsForm] = useState({
    date: '',
    base_price: '350',
    is_available: true,
    is_reserved: false,
    notes: ''
  });

  useEffect(() => {
    loadCalendarData();
  }, [sessionToken, currentDate]);

  const loadCalendarData = async () => {
    try {
      // Load bookings
      const { data: bookings } = await supabase
        .from('bookings')
        .select('*')
        .neq('status', 'cancelled');

      // Load calendar settings
      const { data: settings } = await supabase
        .from('calendar_settings')
        .select('*');

      setCalendarData({
        bookings: bookings || [],
        settings: settings || []
      });
    } catch (error) {
      console.error('Error loading calendar data:', error);
    }
  };

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    
    return days;
  };

  const getDateInfo = (date: Date) => {
    const dateStr = date.toISOString().split('T')[0];
    
    const booking = calendarData.bookings.find((b) => 
      dateStr >= b.check_in && dateStr < b.check_out && b.status !== 'cancelled'
    );
    
    const setting = calendarData.settings.find((s) => s.date === dateStr);

    return { booking, setting };
  };

  const handleDateClick = (date: Date) => {
    setSelectedDate(date);
    const dateStr = date.toISOString().split('T')[0];
    const { setting } = getDateInfo(date);
    
    setSettingsForm({
      date: dateStr,
      base_price: setting?.base_price || '350',
      is_available: setting?.is_available ?? true,
      is_reserved: setting?.is_reserved || false,
      notes: setting?.notes || ''
    });
    setIsSettingsModalOpen(true);
  };

  const handleSaveSettings = async () => {
    try {
      // Check if we're trying to reserve a date that's already reserved
      if (settingsForm.is_reserved) {
        const existingSetting = calendarData.settings.find(s => s.date === settingsForm.date);
        if (existingSetting?.is_reserved) {
          alert('This date is already reserved!');
          return;
        }
      }

      const { error } = await supabase
        .from('calendar_settings')
        .upsert({
          date: settingsForm.date,
          base_price: settingsForm.base_price
        }, { onConflict: 'date' })
        .select()
        .single();

      if (error) throw error;

      await loadCalendarData();
      setIsSettingsModalOpen(false);
    } catch (error) {
      console.error('Error saving settings:', error);
    }
  };

  const handleToggleReservation = async (date: string, reserved: boolean) => {
    try {
      // Check if date is already reserved and we're trying to reserve it
      const existingSetting = calendarData.settings.find(s => s.date === date);
      if (reserved && existingSetting?.is_reserved) {
        alert('This date is already reserved!');
        return;
      }

      const { error } = await supabase
        .from('calendar_settings')
        .upsert({
          date,
          is_reserved: reserved
        }, { onConflict: 'date' })
        .select()
        .single();

      if (error) throw error;

      await loadCalendarData();
    } catch (error) {
      console.error('Error toggling reservation:', error);
    }
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      if (direction === 'prev') {
        newDate.setMonth(prev.getMonth() - 1);
      } else {
        newDate.setMonth(prev.getMonth() + 1);
      }
      return newDate;
    });
  };

  const getDayClassName = (date: Date | null) => {
    if (!date) return 'p-2';
    
    const { booking, setting } = getDateInfo(date);
    const isToday = date.toDateString() === new Date().toDateString();
    
    let className = 'p-2 h-24 border border-[#7C8F75]/10 cursor-pointer hover:bg-[#7C8F75]/5 transition-colors relative ';
    
    if (isToday) {
      className += 'ring-2 ring-[#7C8F75] ';
    }
    
    if (booking) {
      className += 'bg-blue-100 ';
    } else if (setting?.is_reserved) {
      className += 'bg-yellow-100 ';
    } else if (setting && !setting.is_available) {
      className += 'bg-gray-100 ';
    }
    
    return className;
  };

  const Modal = ({ isOpen, onClose, title, children }: any) => {
    if (!isOpen) return null;

    return (
      <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
          <div className="p-6 border-b border-[#7C8F75]/10">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-[#333333]">{title}</h2>
              <Button onClick={onClose} variant="ghost" size="icon">
                <X className="h-5 w-5" />
              </Button>
            </div>
          </div>
          <div className="p-6">
            {children}
          </div>
        </div>
      </div>
    );
  };

  const days = getDaysInMonth(currentDate);
  const monthYear = currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-[#333333]">Calendar Management</h1>
          <p className="text-[#333333]/70">Manage availability, pricing, and reservations</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Calendar */}
        <div className="lg:col-span-3 overflow-hidden">
          <div className="bg-white rounded-2xl shadow-lg border border-[#7C8F75]/10 p-6">
            {/* Calendar Header */}
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl sm:text-2xl font-bold text-[#333333]">{monthYear}</h2>
              <div className="flex items-center space-x-2">
                <Button
                  onClick={() => navigateMonth('prev')}
                  variant="outline"
                  size="icon"
                  className="border-[#7C8F75] text-[#7C8F75] hover:bg-[#7C8F75] hover:text-white"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  onClick={() => navigateMonth('next')}
                  variant="outline"
                  size="icon"
                  className="border-[#7C8F75] text-[#7C8F75] hover:bg-[#7C8F75] hover:text-white"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Calendar Grid */}
            <div className="grid grid-cols-7 gap-0 border border-[#7C8F75]/10 rounded-lg overflow-hidden min-w-0">
              {/* Day Headers */}
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                <div key={day} className="p-2 sm:p-3 bg-[#F9F6F2] text-center font-semibold text-[#333333] border-b border-[#7C8F75]/10 text-xs sm:text-sm">
                  {day}
                </div>
              ))}
              
              {/* Calendar Days */}
              {days.map((date, index) => (
                <div
                  key={index}
                  className={getDayClassName(date)}
                  onClick={() => date && handleDateClick(date)}
                >
                  {date && (
                    <>
                      <div className="font-semibold text-[#333333] mb-1 text-xs sm:text-sm">
                        {date.getDate()}
                      </div>
                      {(() => {
                        const { booking, setting } = getDateInfo(date);
                        if (booking) {
                          return <div className="text-xs text-blue-600 font-medium hidden sm:block">Booked</div>;
                        }
                        if (setting?.is_reserved) {
                          return <div className="text-xs text-yellow-600 font-medium hidden sm:block">Reserved</div>;
                        }
                        if (setting?.base_price && setting.base_price !== '350') {
                          return <div className="text-xs text-[#7C8F75] font-medium hidden sm:block">${setting.base_price}</div>;
                        }
                        return null;
                      })()}
                    </>
                  )}
                </div>
              ))}
            </div>

            {/* Legend */}
            <div className="mt-6 flex flex-wrap gap-4 text-sm">
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 bg-blue-100 border border-blue-200 rounded"></div>
                <span className="text-[#333333]">Booked</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 bg-yellow-100 border border-yellow-200 rounded"></div>
                <span className="text-[#333333]">Reserved</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 bg-white border border-[#7C8F75] rounded"></div>
                <span className="text-[#333333]">Available</span>
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Quick Stats */}
          <div className="bg-white rounded-2xl shadow-lg border border-[#7C8F75]/10 p-6">
            <h3 className="text-lg font-bold text-[#333333] mb-4">This Month</h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-[#333333]/70">Bookings</span>
                <span className="font-semibold text-[#333333]">
                  {calendarData.bookings.filter((b) => {
                    const bookingDate = new Date(b.check_in);
                    return bookingDate.getMonth() === currentDate.getMonth() && 
                           bookingDate.getFullYear() === currentDate.getFullYear();
                  }).length}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#333333]/70">Reserved Days</span>
                <span className="font-semibold text-[#333333]">
                  {calendarData.settings.filter((s) => s.is_reserved).length}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#333333]/70">Custom Pricing</span>
                <span className="font-semibold text-[#333333]">
                  {calendarData.settings.filter((s) => s.base_price !== '350').length}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Date Settings Modal */}
      <Modal
        isOpen={isSettingsModalOpen}
        onClose={() => setIsSettingsModalOpen(false)}
        title={`Settings for ${selectedDate?.toLocaleDateString()}`}
      >
        <div className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="price">Base Price</Label>
            <div className="relative">
              <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-[#333333]/40" />
              <Input
                id="price"
                type="number"
                value={settingsForm.base_price}
                onChange={(e) => setSettingsForm({ ...settingsForm, base_price: e.target.value })}
                className="pl-10 border-[#7C8F75]/30 focus:border-[#7C8F75]"
                placeholder="350"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              value={settingsForm.notes}
              onChange={(e) => setSettingsForm({ ...settingsForm, notes: e.target.value })}
              className="border-[#7C8F75]/30 focus:border-[#7C8F75]"
              rows={3}
              placeholder="Add any notes for this date..."
            />
          </div>

          <div className="flex justify-between">
            <Button
              onClick={() => handleToggleReservation(settingsForm.date, !settingsForm.is_reserved)}
              variant="outline"
              className={`border-purple-500 text-purple-600 hover:bg-purple-50 ${
                settingsForm.is_reserved ? 'bg-yellow-50' : ''
              } ${settingsForm.is_reserved ? 'opacity-50 cursor-not-allowed' : ''}`}
              disabled={settingsForm.is_reserved}
            >
              {settingsForm.is_reserved ? 'Already Reserved' : 'Reserve Date'}
            </Button>
          </div>

          <div className="flex justify-end space-x-4">
            <Button
              onClick={() => setIsSettingsModalOpen(false)}
              variant="outline"
              className="border-[#7C8F75] text-[#7C8F75]"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveSettings}
              className="bg-gradient-to-r from-[#7C8F75] to-[#A8B89A] text-white"
            >
              Save Settings
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default AdminCalendar;